/**
 * Updates the recently viewed products in localStorage.
 *
 * Entries expire after {@link RecentlyViewed.TTL_MS} so the predictive-search
 * "Recently Viewed" dropdown stays genuinely *recent*. Older entries are dropped
 * on read; an entry that has gone stale is not surfaced to consumers.
 *
 * Storage format (internal):
 *   [{ id: string, viewedAt: number }, ...]
 *
 * Public API still returns `string[]` of product IDs so consumers
 * (predictive-search.js) don't need to change.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;
  /** @static @constant {number} Entries older than this (ms) are discarded. 7 days. */
  static TTL_MS = 7 * 24 * 60 * 60 * 1000;

  /**
   * Reads + parses raw storage. Migrates the legacy `string[]` format to the
   * new `{id, viewedAt}[]` shape on the fly, stamping migrated entries with
   * "now" so a returning user does not lose their existing list on first read
   * after this code ships.
   *
   * @returns {Array<{id: string, viewedAt: number}>}
   */
  static #readRaw() {
    let raw;
    try {
      raw = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
    if (!Array.isArray(raw)) return [];

    const now = Date.now();
    return raw
      .map((entry) => {
        if (typeof entry === 'string') return { id: entry, viewedAt: now };
        if (entry && typeof entry === 'object' && typeof entry.id === 'string') {
          return {
            id: entry.id,
            viewedAt: typeof entry.viewedAt === 'number' ? entry.viewedAt : now,
          };
        }
        return null;
      })
      .filter(/** @returns {entry is {id: string, viewedAt: number}} */ (entry) => entry !== null);
  }

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    const now = Date.now();
    const existing = this.#readRaw().filter((entry) => entry.id !== productId);
    const next = [{ id: productId, viewedAt: now }, ...existing].slice(0, this.#MAX_PRODUCTS);
    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* localStorage unavailable (private mode quota / disabled) — silent no-op */
    }
  }

  static clearProducts() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
    } catch {
      /* silent no-op */
    }
  }

  /**
   * Retrieves the list of recently viewed products, with expired entries dropped.
   * @returns {string[]} The list of product IDs newer than TTL_MS.
   */
  static getProducts() {
    const cutoff = Date.now() - this.TTL_MS;
    return this.#readRaw()
      .filter((entry) => entry.viewedAt >= cutoff)
      .map((entry) => entry.id);
  }
}
